import React, { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useI18n } from '@locales/client';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { BodyTextRegularSecondary } from '@/shared/ui/typography';
import { Box } from '@/shared/ui/box';
import { useBanners } from '@/entities/minibanner/model/hooks';
import { LazyImage } from '@/shared/ui/lazy-image/lazy-image';
import { GameFilter } from '@/widgets/game-filter';
import { GameSections } from '@/widgets/game-sections';
import { SelectLanguage } from '@/features/select-language';
import {
  CloseIcon,
  Wrapper,
  MenuList,
  MenuItem,
  SelectIconImage,
  CounterBlock,
  Overlay,
  StyledAlertDialogContent,
  Header,
} from './styles';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';

const MenuModal = ({ visible, onClose, supportOpen }) => {
  const t = useI18n();
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParam = searchParams.get('search');
  const pathname = usePathname();
  const { sortedMobileBanners, loading, linkMobileBanners } = useBanners(true);
  const isLandscape = useMediaQuery('(orientation: landscape)');

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [visible]);

  if (loading) {
    return;
  }

  const handleClose = (open) => {
    if (!!open) {
      onClose();
      if (searchParams.size > 0) {
        router.replace(pathname, { scroll: false });
      }
    }
  };

  const handleClick = (banner) => {
    if (banner.disabled) return;
    if (banner.id === 14) return supportOpen();

    const link = linkMobileBanners(banner);
    if (!link) return;
    if (pathname === link) return onClose();

    router.push(link);
  };

  return (
    <Dialog.Root open={visible} onOpenChange={handleClose}>
      <Dialog.Portal>
        <VisuallyHidden>
          <Dialog.Title>MenuModal</Dialog.Title>
        </VisuallyHidden>
        <Overlay />
        <StyledAlertDialogContent>
          <Header>
            <SelectIconImage>
              <SelectLanguage
                alignToLeft={true}
                isValueMode={true}
                inMobileMenu={true}
              />
            </SelectIconImage>
            {isLandscape && <GameFilter isMobileMenu={true} />}
            <CloseIcon
              width={32}
              onClick={handleClose}
              height={32}
              src="/images/icons/close-dialog.svg"
            />
          </Header>

          <Wrapper>
            {!isLandscape && <GameFilter isMobileMenu={true} />}
            <GameSections isMobileMenu={true} />

            {!searchParam && (
              <MenuList>
                {sortedMobileBanners.map((banner) => (
                  <MenuItem
                    key={banner.id}
                    onClick={() => handleClick(banner)}
                    $isDisabled={banner.disabled}
                  >
                    <Box $width={58} $height={58}>
                      <LazyImage fill src={banner.image} />
                    </Box>
                    {!!banner.counter && (
                      <CounterBlock>{banner.counter}</CounterBlock>
                    )}
                    <BodyTextRegularSecondary style={{ marginTop: '6px' }}>
                      {t(banner.tKey)}
                    </BodyTextRegularSecondary>
                  </MenuItem>
                ))}
              </MenuList>
            )}
          </Wrapper>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { MenuModal };
